import React, { useEffect, useRef } from 'react';
import { formatRecords, formatStorage, edgeKindText } from './format';

const ARROWS = ['lookup', 'master_detail', 'manual'];
const kindClass = (kind) => String(kind || 'LOOKUP').toLowerCase();
const arrowFor = (kind) => {
  const k = kindClass(kind);
  return ARROWS.includes(k) ? k : 'lookup';
};

/**
 * Renders a `layoutNetwork` result as SVG. Purely presentational: no fetching, no layout. The
 * SVG keeps its laid-out pixel width (the wrapper scrolls), so a wide ring never squashes labels.
 */
const NetworkDiagram = ({ layout, onNodeClick, picked = null }) => {
  const canvasRef = useRef(null);
  const centerX = layout && layout.nodes ? (layout.nodes.find((n) => n.isCenter) || {}).x : undefined;
  // A wide ring makes the SVG wider than the panel; open it scrolled to the focal object.
  useEffect(() => {
    const el = canvasRef.current;
    if (el && centerX != null) el.scrollLeft = Math.max(0, centerX - el.clientWidth / 2);
  }, [centerX, layout]);
  if (!layout || !layout.nodes || layout.nodes.length === 0) return null;
  const { width, height, nodes, edges, selfLoops } = layout;
  const center = nodes.find((n) => n.isCenter);
  const rings = new Map();
  nodes.forEach((n) => { if (n.layer !== 0 && !rings.has(n.layer)) rings.set(n.layer, n.y); });

  const nodeTitle = (n) => {
    if (n.overflow) return `${n.overflowCount} more objects on this ring are not drawn (smallest by records)`;
    const lines = [
      n.fullLabel && n.fullLabel !== n.table ? `${n.fullLabel} (${n.table})` : n.table,
      `records: ${formatRecords(n.rowCount)}`,
      `est. storage: ${formatStorage(n.sizeBytes)}`,
    ];
    if (n.keyPrefix) lines.push(`key prefix: ${n.keyPrefix}`);
    if (n.custom) lines.push('custom object');
    if (n.fields && n.fields.length) lines.push(`linked via: ${n.fields.join(', ')}`);
    if (picked && picked.size > 0) lines.push(picked.has(n.table) ? 'chosen' : 'not chosen: discovery skips it');
    return lines.join('\n');
  };
  const edgeTitle = (e) => {
    const from = nodes.find((n) => n.key === e.from);
    const to = nodes.find((n) => n.key === e.to);
    const fields = e.columns.map((c) => `${from ? from.table : e.from}.${c}`).join(', ');
    return `${fields} → ${to ? to.table : e.to} · ${edgeKindText(e.kind)}${e.grouped ? ` (polymorphic ${e.groupLabel})` : ''}`;
  };

  return (
    <div className='sfn-canvas' data-testid='sfn-canvas' ref={canvasRef}>
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} role='img' aria-label={`Objects linked to ${center ? center.table : ''}`}>
        <defs>
          {ARROWS.map((k) => (
            <marker key={k} id={`sfn-arrow-${k}`} viewBox='0 0 10 10' refX='9' refY='5' markerWidth='6' markerHeight='6' orient='auto'>
              <path d='M1 1L9 5L1 9z' className={`sfn-arrow--${k}`} />
            </marker>
          ))}
        </defs>
        {[...rings.entries()].map(([layer, y]) => (
          <g key={`ring-${layer}`}>
            <line className='sfn-ring' x1={8} x2={width - 8} y1={y} y2={y} />
            <text className='sfn-ring-label' x={10} y={y - 6}>
              {layer < 0 ? 'looks up to' : 'referenced by'} · {Math.abs(layer)} hop{Math.abs(layer) > 1 ? 's' : ''}
            </text>
          </g>
        ))}
        {edges.map((e) => (
          <g key={e.id}>
            <line
              className={`sfn-edge sfn-edge--${kindClass(e.kind)}${e.grouped ? ' sfn-edge--grouped' : ''}${e.status === 'REJECTED' ? ' sfn-edge--rejected' : ''}`}
              x1={e.x1} y1={e.y1} x2={e.x2} y2={e.y2}
              markerEnd={`url(#sfn-arrow-${arrowFor(e.kind)})`}
              data-testid={`sfn-edge-${e.from}-${e.to}`}
            >
              <title>{edgeTitle(e)}</title>
            </line>
            {e.labelled && (
              <text className='sfn-edge-label' x={e.lx} y={e.ly}>
                {e.grouped ? e.groupLabel : e.columns.join(', ')}
              </text>
            )}
          </g>
        ))}
        {selfLoops.map((s, i) => center && (
          <g key={`self-${i}`}>
            <path
              className={`sfn-edge sfn-edge--${kindClass(s.kind)}`}
              d={`M ${center.x + center.r * 0.5} ${center.y - center.r * 0.87} a 14 14 0 1 1 ${center.r * 0.5} ${center.r * 0.6}`}
              markerEnd={`url(#sfn-arrow-${arrowFor(s.kind)})`}
            >
              <title>{`${center.table}.${s.columns.join(', ')} → ${center.table} (self reference)`}</title>
            </path>
            <text className='sfn-edge-label' x={center.x + center.r + 22} y={center.y - center.r - 8}>{s.columns.join(', ')}</text>
          </g>
        ))}
        {nodes.map((n) => {
          const unpicked = picked && picked.size > 0 && !n.overflow && !picked.has(n.table);
          const cls = ['sfn-node', n.isCenter ? 'sfn-node--center' : '', n.custom ? 'sfn-node--custom' : '',
            n.untracked && !n.overflow ? 'sfn-node--untracked' : '', n.overflow ? 'sfn-node--overflow' : '',
            unpicked ? 'sfn-node--unpicked' : ''].filter(Boolean).join(' ');
          const clickable = !n.overflow && !n.isCenter && typeof onNodeClick === 'function';
          return (
            <g
              key={n.key}
              className={cls}
              data-testid={`sfn-node-${n.table}`}
              role={clickable ? 'button' : undefined}
              tabIndex={clickable ? 0 : undefined}
              onClick={clickable ? () => onNodeClick(n) : undefined}
              onKeyDown={clickable ? (ev) => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); onNodeClick(n); } } : undefined}
            >
              <title>{nodeTitle(n)}</title>
              {n.overflow
                ? <rect x={n.x - 34} y={n.y - 11} width={68} height={22} rx={11} />
                : <circle cx={n.x} cy={n.y} r={n.r} />}
              <text className='sfn-node-label' x={n.x} y={n.overflow ? n.y + 4 : n.y + n.r + 13}>{n.label}</text>
            </g>
          );
        })}
      </svg>
    </div>
  );
};

export default NetworkDiagram;
